import { Component, inject, input, output, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { IconComponent } from '@digitalware-rs/dw-components';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChildNavItem, NAV_ITEMS, NavItem } from '../../navigation/nav-items';

@Component({
  selector: 'app-sidebar',
  imports: [
    RouterLink,
    RouterLinkActive,
    MatExpansionModule,
    MatButtonModule,
    IconComponent,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  host: { '[class.collapsed]': 'collapsed()' },
})
export class SidebarComponent {
  private readonly router = inject(Router);
  protected translate = inject(TranslateService);

  collapsed = input<boolean>(false);
  expandRequest = output<string>();

  private readonly userExpandedItems = signal(new Set<string>());

  protected navItems = NAV_ITEMS;

  protected hasChildren(item: NavItem): item is NavItem & { children: ChildNavItem[] } {
    return !!item.children?.length;
  }

  protected isParentActive(item: NavItem): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return (
      item.children?.some(child =>
        child.exact ? currentUrl === child.route : currentUrl.startsWith(child.route),
      ) ?? false
    );
  }

  protected isPanelExpanded(item: NavItem): boolean {
    return this.isParentActive(item) || this.userExpandedItems().has(item.labelKey);
  }

  protected onPanelToggled(labelKey: string, opened: boolean): void {
    const set = new Set(this.userExpandedItems());
    if (opened) {
      set.add(labelKey);
    } else {
      set.delete(labelKey);
    }
    this.userExpandedItems.set(set);
  }

  protected onCollapsedParentClick(item: NavItem): void {
    const set = new Set(this.userExpandedItems());
    set.add(item.labelKey);
    this.userExpandedItems.set(set);
    this.expandRequest.emit(item.labelKey);
  }
}
